import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { LifecycleEmailEditor } from "@/components/admin/LifecycleSettings";

export default function ContestEmails() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roles) {
        navigate("/admin");
        return;
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  const { data: contest, isLoading } = useQuery({
    queryKey: ["admin-contest", slug],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const { data: membership, error: memErr } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", session.user.id)
        .limit(1)
        .maybeSingle();

      if (memErr || !membership) throw new Error("Organization not found");

      const { data, error } = await supabase
        .from("contests")
        .select("id, slug, name, organization_id")
        .eq("slug", slug)
        .eq("organization_id", membership.organization_id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Contest not found");
      return data;
    },
    enabled: !isCheckingAuth && !!slug,
  });

  if (isCheckingAuth || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold mb-2">Contest Not Found</h1>
          <Button asChild variant="outline">
            <Link to="/admin/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <Button asChild variant="outline" size="sm" className="mb-4">
            <Link to={`/admin/contests/${contest.slug}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Giveaway
            </Link>
          </Button>
          <h1 className="font-heading text-2xl font-bold">Emails</h1>
          <p className="text-muted-foreground">{contest.name}</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="bg-card rounded-xl border border-border p-6">
          <p className="text-sm text-muted-foreground mb-4">These emails belong to this giveaway. Other giveaways keep their own copy.</p>
          <LifecycleEmailEditor organizationId={contest.organization_id} contestId={contest.id} />
        </div>
      </main>
    </div>
  );
}
